import React, { useState, useEffect, useRef } from 'react';
import { Table, Input } from '@mui/joy';
import CustomPagination from '../components/CustomPagination';
import { truckWeightService } from '../api/truckWeight';
import { PAGE_TRANSLATIONS } from '../conf';
import { formatThaiDate, formatWeight } from '@/util';

const TruckWeightPage: React.FC = () => {
  const [truckWeights, setTruckWeights] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const fetchInitiatedRef = useRef(false);

  const constructWhereData = (searchText: string, startDate: string, endDate: string) => {
    let where = "1=1";
    if (searchText) where = "(_.headLicensePlate ILIKE :searchText OR _.tailLicensePlate ILIKE :searchText)";
    if (startDate) where += " AND _.dateTime >= :startDate";
    if (endDate) where += " AND _.dateTime <= :endDate";
    
    const params = {
      ...(searchText && { searchText: `%${searchText}%` }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate })
    };
    return { where, params };
  };

  useEffect(() => {
    // Prevent double call in StrictMode on initial mount
    if (fetchInitiatedRef.current) {
      return; // Skip if already initiated
    }
    fetchInitiatedRef.current = true; // Mark as initiated for subsequent StrictMode calls

    const loadTruckWeights = async () => {
      try {
        const whereData = constructWhereData(searchText, startDate, endDate);
        const options = {
          max: rowsPerPage,
          offset: (currentPage - 1) * rowsPerPage,
          sort: '_.dateTime',
          order: 'DESC' as 'DESC'
        };
        const response = await truckWeightService.where(whereData, options);
        setTruckWeights(response.data.data);
        setTotalCount(response.data.totalCount);
      } catch (error) {
        console.error('Failed to load truck weights', error);
      }
    };
    loadTruckWeights();
  }, [currentPage, rowsPerPage, searchText, startDate, endDate]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when rows per page changes
  };

  return (
    <div>
      <h2>{PAGE_TRANSLATIONS.TRUCK_WEIGHT}</h2>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="searchText">{PAGE_TRANSLATIONS.SEARCH}</label>
          <Input
            id="searchText"
            ref={inputRef}
            placeholder={PAGE_TRANSLATIONS.LICENSE_PLATE}
            value={searchText}
            onChange={handleFilterChange}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="startDate">{PAGE_TRANSLATIONS.START_DATE}</label>
          <Input
            id="startDate"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="endDate">{PAGE_TRANSLATIONS.END_DATE}</label>
          <Input
            id="endDate"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
          />
        </div>
      </div>
      <Table>
        <thead>
          <tr>
            {/* <th>ID</th> */}
            <th>{PAGE_TRANSLATIONS.HEAD_LICENSE_PLATE}</th>
            <th>{PAGE_TRANSLATIONS.TAIL_LICENSE_PLATE}</th>
            <th>{PAGE_TRANSLATIONS.TRUCK_WEIGHT}</th>
            <th>{PAGE_TRANSLATIONS.DATE}</th>
            <th>{PAGE_TRANSLATIONS.WEIGHED_BY}</th>
          </tr>
        </thead>
        <tbody>
          {truckWeights.length > 0 ? (
            truckWeights.map((weight) => (
              <tr key={weight.id}>
                {/* <td>{weight.id}</td> */}
                <td>{weight.headLicensePlate}</td>
                <td>{weight.tailLicensePlate}</td>
                <td>{formatWeight(weight.weight)}</td>
                <td>{formatThaiDate(weight.dateTime)}</td>
                <td>{weight.weightBy}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} style={{ textAlign: 'center' }}>{PAGE_TRANSLATIONS.NO_DATA}</td>
            </tr>
          )}
        </tbody>
      </Table>
      <CustomPagination
        count={totalCount}
        page={totalCount > 0 ? currentPage : 0}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  );
};

export default TruckWeightPage; 